import { useCallback } from 'react'
import styled from 'styled-components'

import { formatFileName } from '../lib/formatters'
import { ClosingX } from '../shared/ClosingX'
import { ProgressBar } from '../shared/ProgressBar'
import { Spinner } from '../shared/Spinner'
import { IconButton } from '../shared/styledComponents'
import { TdsFileNameTooltip } from '../shared/TdsFileNameTooltip'
import { PhotoPreview } from './TdsPhotoPreviewModal'

const Spin = styled(Spinner)`
  margin: 5px 5px 0 5px;
`

const Image = styled.img`
  height: 25px;
  width: 25px;
  margin: 15px 8px 0px 20px;
  flex-basis: auto;
  flex-wrap: wrap;
`
const TitleSpan = styled.div`
  margin-top: 8px;
  @media (max-width: 468px) {
    font-size: 11px;
  }
`
const Preview = styled(IconButton)`
  min-height: 40px;
`
const Delete = styled(IconButton)`
  margin: 0 11px 0 0;
  min-height: 40px;
  line-height: 0;
  font-size: 0;
`
const Checkmark = styled.span`
  display: inline-block;
  width: 28px;
  height: 5px;
  margin-right: 15px;
  margin-bottom: 5px;
  -ms-transform: rotate(45deg);
  -webkit-transform: rotate(45deg);
  transform: rotate(45deg);
`
const CheckmarkStem = styled.span`
  position: absolute;
  width: 3px;
  height: 19px;
  background-color: #0caf51;
  left: 18px;
  top: -4px;
`
const CheckmarkKick = styled.span`
  position: absolute;
  width: 12px;
  height: 3px;
  background-color: #0caf51;
  left: 9px;
  top: 13px;
`
const Container = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  border: 1px solid silver;
  border-top: none;
  width: 100%;
`
const SubContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  flex-wrap: nowrap;
  flex: 0 1 auto;
`

const Top = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: space-between;
  align-items: center;
  margin: 4px 0;
`

const IconContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: space-around;
  align-items: center;
`

const ProgressContainer = styled.div`
  margin-top: 2px;
`

type PreviewCardProps = {
  id?: string
  src?: string
  progress: number
  timeLeft?: string
  mimeType?: string
  size: number
  error?: {
    message: string
  }
  title: string
  handleDelete(id?: string): void
  handleDisplay(title: string): void
  handleClose(): void
  displayInModal: boolean
}

const PreviewCard = (props: PreviewCardProps) => {
  const {
    id,
    src,
    progress,
    timeLeft,
    mimeType,
    title,
    size,
    error,
    handleClose,
    handleDelete,
    handleDisplay
  } = props
  const onDelete = useCallback(() => handleDelete(id), [id, handleDelete])
  const onShowDisplay = useCallback(() => handleDisplay(title), [title, handleDisplay])
  const getPreviewId = useCallback(() => 'preview-' + id, [id])
  return (
    <Container className="preview-card" role='region'>
      {src ? <Image src={src} alt={title} className="tlPrivate" /> : <Spin />}
      <SubContainer>
        <Top>
          <TitleSpan className="tlPrivate">
            {formatFileName(title, size)}
          </TitleSpan>
          <TdsFileNameTooltip fileName={title} />
          <IconContainer>
            {progress <= 0 && (/image/).test(mimeType ?? '') &&
              <Preview
                data-trigger-modal={getPreviewId()}
                onClick={onShowDisplay}
                key={id}
              >
                <span>
                  <i
                    className="sprite sprite-Photo_preview"
                    aria-label="Preview"
                    role="button"
                    title="Preview"
                  />
                </span>

              </Preview>
            }
            {progress <= 0 && (
              <Delete className="delete-button" onClick={onDelete} aria-label="Delete">
                <ClosingX width="18px" height="18px" />
              </Delete>
            )}
            <PhotoPreview
              alt={title}
              src={src}
              handleClose={handleClose}
              id={getPreviewId()}
            />

            {!error && progress === 100 && (
              <Checkmark>
                <CheckmarkStem />
                <CheckmarkKick />
              </Checkmark>
            )}
          </IconContainer>
        </Top>
        <ProgressContainer>
          {(progress > 0 || error) && (
            <ProgressBar progress={progress} error={error} timeLeft={timeLeft} />
          )}
        </ProgressContainer>
      </SubContainer>

    </Container>
  )
}

export { PreviewCard }
